import { Alert, AlertTitle, Box, Collapse, Container, Typography } from '@mui/material';
import { PaymentOutlined } from '@mui/icons-material';
import { useFormikContext } from 'formik';
import AddressLookup from '@acorn-portal/shared/src/components/addressLookup';
import FormFieldOptions from '@acorn-portal/shared/src/components/inputs/FormFieldOptions';
import RadioGroupButton from '@acorn-portal/shared/src/components/inputs/radio-button-group';
import FormTextField from '@acorn-portal/shared/src/components/inputs/FormTextField';
import { Address } from '@acorn-portal/shared/src/services/api';
import { FC } from 'react';
import { IRenewalMakePaymentForm } from '@acorn-portal/shared/src/pages/renewal/MakePayment';
import { FieldSet } from '@acorn-portal/shared/src/components/fieldSet';
import { LoadingButton } from '@mui/lab';
import DirectPaymentVendors from '@acorn-portal/shared/src/components/vendor/DirectPaymentVendors';
import { useAppSelector } from '../../hooks/reduxHook';
import DebitCardSelector from '../payment/DebitCardSelector';
import BraintreeFormInput from '../payment/braintree-form-input';
import { useBraintreeContext } from '@acorn-portal/shared/src/context/braintree-context';

export interface BraintreeCardDetails {
	billingDetails?: 'yes' | 'no';
	cardholderFirstName: string;
	cardholderLastName: string;
	cardholderEmail: string;
	address: Address;
	saveCard: boolean;
}

interface IBraintreePaymentForm {
	submitting?: boolean;
	useExistingCard?: boolean;
	amount: string;
	RenewalPaymentError: string | null;
}

const BraintreePaymentForm: FC<IBraintreePaymentForm> = ({
	submitting,
	useExistingCard,
	amount,
	RenewalPaymentError,
}) => {
	const renewal = useAppSelector((x) => x.renewal);
	const { renewalDetails } = useAppSelector((x) => x.fullPolicy);

	const formik = useFormikContext<IRenewalMakePaymentForm>();
	const { isPaymentDetailsValid } = useBraintreeContext();

	return (
		<FieldSet title=''>
			<Collapse
				in={useExistingCard}
				sx={{
					'* > svg': {
						visibility: 'visible !important',
					},
				}}
			>
				<Box
					sx={{
						margin: 'auto',
						maxWidth: '500px',
						textAlign: 'center',
						pb: '1.5em',
					}}
				>
					{renewal.renewalRatingResponse?.existingCardPaymentMethods && (
						<DebitCardSelector cards={renewal.renewalRatingResponse.existingCardPaymentMethods} />
					)}
				</Box>
			</Collapse>
			<Collapse
				in={!useExistingCard}
				sx={{
					'* > svg': {
						visibility: 'visible !important',
					},
				}}
			>
				<RadioGroupButton
					label='Is the debit/credit card registered to the same address?'
					name='billingDetails'
					id='billingDetails'
					value={formik.values.billingDetails}
					onblur={() => {
						formik.validateField('address');
						formik.validateField('cardholderFirstName');
						formik.validateField('cardholderLastName');
						formik.validateField('cardholderEmail');
					}}
					onChange={(value) => {
						if (value === 'yes') {
							const policyHolder = renewalDetails?.policyHolder;
							if (!policyHolder) return;
							formik.setValues({
								...formik.values,
								billingDetails: value,
								cardholderFirstName: policyHolder.firstName,
								cardholderLastName: policyHolder.lastName,
								cardholderEmail: policyHolder.email,
								address: policyHolder.address,
							});
						} else {
							formik.setValues({
								...formik.values,
								billingDetails: value as 'yes' | 'no' | undefined,
								cardholderFirstName: '',
								cardholderLastName: '',
								cardholderEmail: '',
								address: {
									line1: '',
									line2: '',
									city: '',
									postcode: '',
								},
							});
						}
					}}
					error={formik.touched.billingDetails && Boolean(formik.errors.billingDetails)}
					helperText={formik.touched.billingDetails && formik.errors.billingDetails}
				/>
				<Collapse in={formik.values.billingDetails === 'no'}>
					<Box>
						<Typography variant='h6' gutterBottom sx={{ textAlign: 'left', fontSize: 18 }}>
							Billing Details
						</Typography>
						<FormTextField
							id='cardholderFirstName'
							name='cardholderFirstName'
							label='Cardholder First Name'
							value={formik.values.cardholderFirstName}
							onChange={formik.handleChange}
							error={formik.touched.cardholderFirstName && Boolean(formik.errors.cardholderFirstName)}
							helperText={formik.touched.cardholderFirstName && formik.errors.cardholderFirstName}
						/>
						<FormTextField
							id='cardholderLastName'
							name='cardholderLastName'
							label='Cardholder Last Name'
							value={formik.values.cardholderLastName}
							onChange={formik.handleChange}
							error={formik.touched.cardholderFirstName && Boolean(formik.errors.cardholderLastName)}
							helperText={formik.touched.cardholderLastName && formik.errors.cardholderLastName}
						/>
						<FormTextField
							autoComplete='email'
							format='email'
							id='cardholderEmail'
							name='cardholderEmail'
							label='Cardholder Email'
							value={formik.values.cardholderEmail}
							onChange={formik.handleChange}
							error={formik.touched.cardholderEmail && Boolean(formik.errors.cardholderEmail)}
							helperText={formik.touched.cardholderEmail && formik.errors.cardholderEmail}
						/>
						<Typography variant='h6' gutterBottom sx={{ textAlign: 'left', fontSize: 18 }}>
							Billing Address
						</Typography>
						<AddressLookup
							initialValue={formik.values.address}
							onChange={(value) => {
								formik.setFieldValue('address', value);
							}}
							globalError={formik.touched.address && Boolean(formik.errors.address)}
							firstFieldLabel='Cardholder house number or name'
							postcodeLabel='Cardholder postcode'
						/>
					</Box>
				</Collapse>
				<Box sx={{ mt: 2 }}>
					<FormFieldOptions
						noMarginBottom
						name='saveCard'
						label=''
						value={formik.values.saveCard}
						onChange={(event) => {
							const { value } = event.target;
							formik.setFieldValue('saveCard', value === 'true');
						}}
						options={[
							{
								label: (
									<Typography>
										<b>Save my card details</b> - We will keep your card details stored safely and securely to allow us
										to take any future payments
									</Typography>
								),
								value: true,
							},
							{
								label: (
									<Typography>
										<b>Don&apos;t save my card details</b> - We won&apos;t store your card details and you&apos;ll need
										to give us your payment card details every time you make a change
									</Typography>
								),
								value: false,
							},
						]}
					/>
				</Box>
				<BraintreeFormInput amount={amount} isOptOut={!formik.values.saveCard} noPayPal />
				{RenewalPaymentError && (
					<Container maxWidth='sm' sx={{ mt: 2, textAlign: 'left' }}>
						<Alert severity='error'>
							<AlertTitle>Error</AlertTitle>
							{RenewalPaymentError}
						</Alert>
					</Container>
				)}
			</Collapse>
			<Box
				sx={{
					textAlign: 'center',
					maxWidth: '400px',
					mt: 4,
					mr: 'auto',
					ml: 'auto',
				}}
			>
				<LoadingButton
					variant='contained'
					color='secondary'
					fullWidth
					size='large'
					startIcon={<PaymentOutlined />}
					type='submit'
					loading={submitting}
					disabled={!isPaymentDetailsValid}
				>
					Make Payment
				</LoadingButton>

				<Box
					sx={{
						maxWidth: 500,
						textAlign: 'center',
						margin: 'auto',
					}}
				>
					<DirectPaymentVendors />
				</Box>
			</Box>
		</FieldSet>
	);
};

export default BraintreePaymentForm;
